/**
 * modularFirestorePaths.js
 *
 * Module-parameterised Firestore path builders for any subcollection-based
 * assessment pathway. This replaces the hardcoded 'adult_autism' segments
 * that were originally in adultAutismFirestorePaths.js, while keeping the
 * same path structure.
 *
 * Path layout (for any modular module):
 *   patients/{patientUid}/{collection}/{assessmentDocId}
 *   patients/{patientUid}/{collection}/{assessmentDocId}/formInstances/{formKey}
 *   patients/{patientUid}/{collection}/{assessmentDocId}/assessmentStages/{stageKey}
 *   patients/{patientUid}/{collection}/{assessmentDocId}/reportContent/{stageKey}
 *
 *   informants/{informantUid}/patients/{patientUid}/{collection}/{assessmentDocId}
 *   informants/{informantUid}/patients/{patientUid}/{collection}/{assessmentDocId}/formInstances/{formKey}
 *   informants/{informantUid}/patients/{patientUid}/{collection}/{assessmentDocId}/assessmentStages/{stageKey}
 *
 * Callers resolve the collection + assessmentDocId from the module registry
 * (src/constants/assessmentModules.js) and pass them here. Alternatively,
 * use the convenience wrappers that accept an assessmentModule key directly.
 */

import { getModuleCollection, getModuleAssessmentDocId } from './assessmentModules';

// ─── Low-level builders (collection + docId passed explicitly) ───────────

/** @returns {string[]} Path segments for the patient header (module-agnostic). */
export function getPatientHeaderPathSegments(patientUid) {
  return ['patients', patientUid];
}

/** @returns {string[]} */
export function getAssessmentDocPathSegments(patientUid, moduleCollection, assessmentDocId) {
  return ['patients', patientUid, moduleCollection, assessmentDocId];
}

/** @returns {string[]} */
export function getFormInstancePathSegments(patientUid, moduleCollection, assessmentDocId, formKey) {
  return ['patients', patientUid, moduleCollection, assessmentDocId, 'formInstances', formKey];
}

/** @returns {string[]} */
export function getAssessmentStagePathSegments(patientUid, moduleCollection, assessmentDocId, stageKey) {
  return ['patients', patientUid, moduleCollection, assessmentDocId, 'assessmentStages', stageKey];
}

/** @returns {string[]} */
export function getReportContentPathSegments(patientUid, moduleCollection, assessmentDocId, stageKey) {
  return ['patients', patientUid, moduleCollection, assessmentDocId, 'reportContent', stageKey];
}

// ─── Informant tree equivalents ──────────────────────────────────────────

/** @returns {string[]} */
export function getInformantAssessmentBaseSegments(informantUid, patientUid, moduleCollection, assessmentDocId) {
  return ['informants', informantUid, 'patients', patientUid, moduleCollection, assessmentDocId];
}

/** @returns {string[]} */
export function getInformantFormInstancePathSegments(informantUid, patientUid, moduleCollection, assessmentDocId, formKey) {
  return [
    'informants', informantUid, 'patients', patientUid,
    moduleCollection, assessmentDocId, 'formInstances', formKey,
  ];
}

/** @returns {string[]} */
export function getInformantAssessmentStagePathSegments(informantUid, patientUid, moduleCollection, assessmentDocId, stageKey) {
  return [
    'informants', informantUid, 'patients', patientUid,
    moduleCollection, assessmentDocId, 'assessmentStages', stageKey,
  ];
}

// ─── Convenience wrappers (assessmentModule key → resolved paths) ────────

/**
 * Resolve the assessment base path segments for a patient, given a module key.
 * @param {string} patientUid
 * @param {string} assessmentModule - e.g. 'adultAutism', 'cypAdhd'
 * @returns {string[]}
 */
export function getModularAssessmentBase(patientUid, assessmentModule) {
  const col = getModuleCollection(assessmentModule);
  const docId = getModuleAssessmentDocId(assessmentModule);
  if (!col || !docId) {
    throw new Error(`Cannot resolve modular paths for non-modular module: ${assessmentModule}`);
  }
  return ['patients', patientUid, col, docId];
}

/**
 * Resolve informant assessment base path segments, given a module key.
 * @param {string} informantUid
 * @param {string} patientUid
 * @param {string} assessmentModule
 * @returns {string[]}
 */
export function getModularInformantAssessmentBase(informantUid, patientUid, assessmentModule) {
  const col = getModuleCollection(assessmentModule);
  const docId = getModuleAssessmentDocId(assessmentModule);
  if (!col || !docId) {
    throw new Error(`Cannot resolve modular informant paths for non-modular module: ${assessmentModule}`);
  }
  return ['informants', informantUid, 'patients', patientUid, col, docId];
}

/**
 * Join path segments into a slash-separated string (useful for assertions in tests).
 * @param {string[]} segments
 * @returns {string}
 */
export function joinFirestorePath(segments) {
  return segments.join('/');
}
